import React, { useCallback, useMemo } from 'react';
import Modal from 'src/modules/shared/components/molecules/modals/modal';
import { ModalFooter } from 'src/modules/shared/components/molecules/modals/modal-footer';
import { Text } from 'src/modules/shared/components/atoms/text';
import Loading from 'src/modules/shared/components/atoms/loading';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import TextInput from 'src/modules/shared/components/atoms/inputs/text-input';
import type { MappingValue } from '../create-trigger-modal/mapping-row';
import { InputMappingSection } from '../shared/input-mapping-section';
import { EmptyState } from 'src/modules/shared/components/atoms/states/empty-state';
import { ErrorState } from 'src/modules/shared/components/atoms/states/error-state';
import { useTemplateSelector } from 'src/modules/automations/hooks/use-template-selector';
import { useTemplateTriggers } from 'src/modules/automations/hooks/use-template-triggers';
import { useTemplateMapping } from 'src/modules/automations/hooks/use-template-mapping';
import { useCreateAutomationFromTemplate } from 'src/modules/automations/hooks/use-create-automation-from-template';
import { useGuidedHandoffConfiguration } from 'src/modules/automations/hooks/use-guided-handoff-configuration';
import { useSemanticQuickAutomationSetup } from 'src/modules/automations/hooks/use-semantic-quick-automation-setup';
import {
  buildInstructions as buildMappingInstructions,
  areRequiredFieldsMapped,
} from 'src/modules/automations/utils/templates/template-mapping.util';
import { validateGuidedHandoffTemplate } from 'src/modules/automations/utils/templates/handoff-template.util';
import {
  isSemanticQuickAutomationMappingValid,
  validateSemanticQuickAutomationTemplate,
} from 'src/modules/automations/utils/templates/semantic-quick-automation.util';
import type { QuickAutomationContext } from 'src/modules/automations/types/quick-automation.type';
import type { SemanticQuickAutomationKind } from 'src/modules/automations/types/semantic-quick-automation.type';
import { GuidedHandoffConfiguration } from './guided-handoff-configuration';
import { SemanticQuickAutomationConfiguration } from './semantic-quick-automation-configuration';
import { useHasPermissions } from 'src/modules/shared/hooks/auth';
import { AutomationEmployeePermission } from 'src/modules/shared/types/permissions.type';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import type { TCTemplateRecord } from 'src/api/generated';

interface CreateAutomationFromTemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated?: () => void;
  presetMapping?: Record<string, MappingValue>;
  quickAutomationContext?: QuickAutomationContext;
  title?: string;
  confirmLabel?: string;
}

interface TemplateCardsProps {
  /** Template summaries available in Templates App. */
  templates: TCTemplateRecord[];
  /** Currently selected template identifier. */
  selectedTemplateId: string;
  /** Whether selection must remain frozen during activation. */
  locked: boolean;
  /** Whether the initial template page is loading. */
  loading: boolean;
  /** Whether another page of templates is available. */
  hasNextPage: boolean;
  /** Whether the next template page is loading. */
  fetchingNextPage: boolean;
  /** Resolves a trigger identifier to its display name. */
  triggerDisplay: (id?: string) => string;
  /** Selects a template detail. */
  onSelect: (templateId: string) => void;
  /** Loads another page of template summaries. */
  onLoadMore: () => void;
}

const TemplateCards: React.FC<TemplateCardsProps> = ({
  templates,
  selectedTemplateId,
  locked,
  loading,
  hasNextPage,
  fetchingNextPage,
  triggerDisplay,
  onSelect,
  onLoadMore,
}) => {
  if (templates.length === 0 && loading) {
    return null;
  }

  if (templates.length === 0) {
    return (
      <EmptyState
        title="Sin plantillas" // TODO: check when implementing i18n
        description="No hay plantillas disponibles para mostrar." // TODO: check when implementing i18n
        appearance="soft"
        variant="neutral"
      />
    );
  }

  return (
    <div className="space-y-2">
      {templates.map((templateItem, index) => {
        const isActive =
          Boolean(templateItem.templateId) &&
          templateItem.templateId === selectedTemplateId;
        const templateKey =
          templateItem.templateId ??
          `${templateItem.key ?? templateItem.name ?? 'template'}-${index}`;

        return (
          <button
            key={templateKey}
            onClick={() => {
              if (templateItem.templateId) {
                onSelect(templateItem.templateId);
              }
            }}
            disabled={locked}
            className={`w-full text-left border rounded-lg p-3 transition-colors ${
              isActive
                ? 'border-primary-500 bg-primary-50'
                : 'border-gray-200 hover:border-primary-300'
            } ${locked ? 'cursor-not-allowed opacity-70' : ''}`}
          >
            <div className="flex items-start justify-between gap-2">
              <div>
                <Text size="small" weight="medium">
                  {templateItem.name}
                </Text>
                <div className="flex flex-wrap items-center gap-2 text-gray-600 text-[11px]">
                  {templateItem.trigger?.id && (
                    <span className="inline-flex items-center rounded-full bg-primary-50 px-2 py-0.5 text-primary-700">
                      Trigger: {triggerDisplay(templateItem.trigger.id)}
                    </span>
                  )}
                </div>
              </div>
            </div>
            {templateItem.tags && templateItem.tags.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-1">
                {templateItem.tags.map((tag: string) => (
                  <span
                    key={tag}
                    className="text-[10px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-700"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            )}
          </button>
        );
      })}
      {hasNextPage && (
        <Button
          variant="secondary"
          size="sm"
          disabled={fetchingNextPage}
          onClick={onLoadMore}
        >
          <Text size="tiny">
            {fetchingNextPage ? 'Cargando...' : 'Cargar más'}
          </Text>
        </Button>
      )}
    </div>
  );
};

interface TemplateDetailProps {
  /** Full selected template, when loaded. */
  template: TCTemplateRecord | null;
  /** Whether template detail is loading. */
  loading: boolean;
  /** Resolved trigger name. */
  triggerName?: string;
}

const TemplateDetail: React.FC<TemplateDetailProps> = ({
  template,
  loading,
  triggerName,
}) => {
  if (loading || !template) {
    return (
      <div className="text-gray-500 text-sm">
        {
          loading
            ? 'Cargando plantilla...' // TODO: check when implementing i18n
            : 'Selecciona una plantilla' // TODO: check when implementing i18n
        }
      </div>
    );
  }

  const triggerLabel = triggerName ?? template.trigger?.id;

  return (
    <div className="space-y-2">
      <Text size="small" weight="semibold" className="text-gray-900">
        {template.name}
      </Text>
      {template.description && (
        <Text size="tiny" className="text-gray-700">
          {template.description}
        </Text>
      )}
      <div className="flex flex-wrap gap-1">
        {template.tags?.map((tag: string) => (
          <span
            key={tag}
            className="text-[10px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-700"
          >
            {tag}
          </span>
        ))}
      </div>
      {template.readme && (
        <div className="max-h-32 overflow-auto border rounded p-2 bg-gray-50">
          <Text size="tiny" className="text-gray-700 whitespace-pre-line">
            {template.readme}
          </Text>
        </div>
      )}
      {triggerLabel && (
        <div className="text-[11px] text-gray-500 space-y-0.5">
          <div>Trigger: {triggerLabel}</div>
        </div>
      )}
    </div>
  );
};

interface ConfigurationEditorProps {
  /** Recognized handoff variant, or null for the generic editor. */
  guidedKind: ReturnType<typeof validateGuidedHandoffTemplate>['kind'];
  /** Whether the recognized template matches the expected contract. */
  guidedTemplateValid: boolean;
  /** Recognized status/User adapter, or null for other templates. */
  semanticKind: ReturnType<
    typeof validateSemanticQuickAutomationTemplate
  >['kind'];
  /** Whether the status/User template matches its exact production contract. */
  semanticTemplateValid: boolean;
  /** Communications context used to scope guided options. */
  context?: QuickAutomationContext;
  /** Guided setup and form state. */
  guidedConfiguration: ReturnType<typeof useGuidedHandoffConfiguration>;
  /** Reference data used by exact status/User adapters. */
  semanticSetup: ReturnType<typeof useSemanticQuickAutomationSetup>;
  /** Current decoded mapping controlled by semantic selectors. */
  mapping: Record<string, MappingValue>;
  /** Replaces the current decoded mapping. */
  onMappingChange: (mapping: Record<string, MappingValue>) => void;
  /** Whether employee permission evaluation is still loading. */
  loadingEmployeePermission: boolean;
  /** Whether the current user can list AI Agents. */
  canReadEmployees: boolean;
  /** Whether inputs must stay frozen during activation. */
  locked: boolean;
  /** Existing generic mapping editor for all other templates. */
  genericEditor: React.ReactNode;
}

interface SemanticConfigurationEditorProps {
  /** Exact semantic adapter selected for the current template. */
  kind: SemanticQuickAutomationKind;
  /** Reference data used by exact status/User adapters. */
  setup: ReturnType<typeof useSemanticQuickAutomationSetup>;
  /** Current decoded mapping controlled by semantic selectors. */
  mapping: Record<string, MappingValue>;
  /** Replaces the current decoded mapping. */
  onMappingChange: (mapping: Record<string, MappingValue>) => void;
  /** Whether inputs must stay frozen during activation. */
  locked: boolean;
}

const SemanticConfigurationEditor: React.FC<
  SemanticConfigurationEditorProps
> = ({ kind, setup, mapping, onMappingChange, locked }) => {
  const { t } = useI18n();

  if (
    kind === 'status-and-person-user' &&
    !setup.usersPermissionLoading &&
    !setup.canReadUsers
  ) {
    return (
      <ErrorState
        size="sm"
        appearance="soft"
        title={t('automations.quickHandoff.errors.userPermissionTitle')}
        description={t(
          'automations.quickHandoff.errors.userPermissionDescription'
        )}
      />
    );
  }

  if (setup.error) {
    return (
      <ErrorState
        size="sm"
        appearance="soft"
        title={t('automations.quickHandoff.errors.setupTitle')}
        description={t('automations.quickHandoff.errors.setupDescription')}
        actionContent={t('automations.quickHandoff.actions.retryLoad')}
        onAction={() => {
          void setup.refetch();
        }}
      />
    );
  }

  return (
    <SemanticQuickAutomationConfiguration
      kind={kind}
      statuses={setup.statuses}
      users={setup.users}
      mapping={mapping}
      labels={{
        status: t('automations.quickAutomation.fields.activationStatus'),
        statusPlaceholder: t(
          'automations.quickAutomation.placeholders.activationStatus'
        ),
        emptyStatuses: t('automations.quickAutomation.empty.statuses'),
        user: t('automations.quickAutomation.fields.assigneeUser'),
        userPlaceholder: t(
          'automations.quickAutomation.placeholders.assigneeUser'
        ),
        emptyUsers: t('automations.quickAutomation.empty.users'),
      }}
      statusesLoading={setup.loading}
      usersLoading={setup.loading}
      disabled={locked}
      onMappingChange={onMappingChange}
    />
  );
};

const ConfigurationEditor: React.FC<ConfigurationEditorProps> = ({
  guidedKind,
  guidedTemplateValid,
  semanticKind,
  semanticTemplateValid,
  context,
  guidedConfiguration,
  semanticSetup,
  mapping,
  onMappingChange,
  loadingEmployeePermission,
  canReadEmployees,
  locked,
  genericEditor,
}) => {
  const { t } = useI18n();

  if (guidedKind === null && semanticKind === null) {
    return genericEditor;
  }

  if (!context?.funnelId) {
    return (
      <ErrorState
        size="sm"
        appearance="soft"
        title={t('automations.quickHandoff.errors.contextTitle')}
        description={t('automations.quickHandoff.errors.contextDescription')}
      />
    );
  }

  if (
    (guidedKind !== null && !guidedTemplateValid) ||
    (semanticKind !== null && !semanticTemplateValid)
  ) {
    return (
      <ErrorState
        size="sm"
        appearance="soft"
        title={t('automations.quickHandoff.errors.templateTitle')}
        description={t(
          guidedKind !== null
            ? 'automations.quickHandoff.errors.templateDescription'
            : 'automations.quickAutomation.errors.templateDescription'
        )}
      />
    );
  }

  if (guidedKind !== null) {
    if (
      !guidedConfiguration.usersPermissionLoading &&
      !guidedConfiguration.canReadUsers
    ) {
      return (
        <ErrorState
          size="sm"
          appearance="soft"
          title={t('automations.quickHandoff.errors.userPermissionTitle')}
          description={t(
            'automations.quickHandoff.errors.userPermissionDescription'
          )}
        />
      );
    }

    return (
      <GuidedHandoffConfiguration
        funnel={guidedConfiguration.funnel}
        statuses={guidedConfiguration.statuses}
        employees={guidedConfiguration.employees}
        kind={guidedKind}
        values={guidedConfiguration.values}
        loading={guidedConfiguration.loading || loadingEmployeePermission}
        error={guidedConfiguration.error}
        canReadEmployees={canReadEmployees}
        disabled={locked}
        onDestinationStatusChange={guidedConfiguration.setDestinationStatusId}
        onEnabledStatusesChange={guidedConfiguration.setEnabledStatusIds}
        onEmployeeChange={guidedConfiguration.setEmployeeId}
        onExcludedContentChange={guidedConfiguration.setExcludedMessageContent}
        onRetry={() => {
          void guidedConfiguration.refetch();
        }}
      />
    );
  }

  if (semanticKind === null) {
    return genericEditor;
  }

  return (
    <SemanticConfigurationEditor
      kind={semanticKind}
      setup={semanticSetup}
      mapping={mapping}
      locked={locked}
      onMappingChange={onMappingChange}
    />
  );
};

export const CreateAutomationFromTemplateModal: React.FC<
  CreateAutomationFromTemplateModalProps
> = ({
  isOpen,
  onClose,
  onCreated,
  presetMapping,
  quickAutomationContext,
  title = 'Crear automatización desde plantilla', // TODO: check when implementing i18n
  confirmLabel = 'Crear automatización', // TODO: check when implementing i18n
}) => {
  const { t } = useI18n();
  const {
    hasPermission: canReadEmployees,
    loading: loadingEmployeePermission,
  } = useHasPermissions(AutomationEmployeePermission.Read);
  const {
    templates,
    templatesLoading,
    templatesError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    selectedTemplateId,
    setSelectedTemplateId,
    selectedTemplate,
    templateLoading,
    workflowName,
    setWorkflowName,
  } = useTemplateSelector({ isOpen });

  const {
    triggersLoading,
    triggerDisplay,
    selectedTrigger,
    selectedTriggerName,
  } = useTemplateTriggers(isOpen, selectedTemplate);

  const {
    mapping,
    setMapping,
    fieldKeys,
    requiredFields,
    fieldExpectedLiteralTypes,
    eventPathOptions,
    allRequiredMapped,
    templateDefinition,
  } = useTemplateMapping({
    selectedTemplate,
    presetMapping,
    triggerDataSchema: selectedTrigger?.triggerDataSchema,
  });

  const guidedValidation = useMemo(
    () => validateGuidedHandoffTemplate(selectedTemplate),
    [selectedTemplate]
  );
  const guidedKind = guidedValidation.kind;
  const semanticValidation = useMemo(
    () => validateSemanticQuickAutomationTemplate(selectedTemplate),
    [selectedTemplate]
  );
  const semanticKind = semanticValidation.kind;
  const guidedConfiguration = useGuidedHandoffConfiguration({
    template: selectedTemplate,
    context: quickAutomationContext,
    kind: guidedKind,
    baseMapping: mapping,
    enabled:
      isOpen &&
      guidedValidation.valid &&
      canReadEmployees &&
      !loadingEmployeePermission &&
      Boolean(quickAutomationContext?.funnelId),
  });
  const semanticSetup = useSemanticQuickAutomationSetup(
    quickAutomationContext?.funnelId,
    semanticKind,
    isOpen &&
      semanticValidation.valid &&
      Boolean(quickAutomationContext?.funnelId)
  );
  const effectiveMapping =
    guidedKind !== null && guidedValidation.valid
      ? guidedConfiguration.mapping
      : mapping;
  const effectiveMappingValid =
    guidedKind !== null
      ? guidedValidation.valid &&
        Boolean(quickAutomationContext?.funnelId) &&
        canReadEmployees &&
        !loadingEmployeePermission &&
        guidedConfiguration.canReadUsers &&
        !guidedConfiguration.usersPermissionLoading &&
        guidedConfiguration.valid &&
        areRequiredFieldsMapped(effectiveMapping, requiredFields)
      : semanticKind !== null
        ? semanticValidation.valid &&
          Boolean(quickAutomationContext?.funnelId) &&
          !semanticSetup.loading &&
          !semanticSetup.error &&
          (semanticKind === 'status-only' || semanticSetup.canReadUsers) &&
          isSemanticQuickAutomationMappingValid(
            mapping,
            semanticSetup.statuses,
            semanticSetup.users,
            semanticKind
          ) &&
          areRequiredFieldsMapped(mapping, requiredFields)
        : allRequiredMapped &&
          areRequiredFieldsMapped(effectiveMapping, requiredFields);
  const buildInstructions = useCallback(
    () => buildMappingInstructions(fieldKeys, effectiveMapping),
    [effectiveMapping, fieldKeys]
  );

  const { onConfirm, creating, pendingAttempt, resetPendingAttempt } =
    useCreateAutomationFromTemplate({
      selectedTemplate,
      templateDefinition,
      workflowName,
      buildInstructions,
      onCreated,
      onClose,
    });
  const configurationLocked = creating || pendingAttempt !== null;

  const handleClose = useCallback(() => {
    if (creating) {
      return;
    }
    if (
      pendingAttempt &&
      !window.confirm(
        t('automations.quickHandoff.closeWarning', {
          workflowId: pendingAttempt.workflowId,
        })
      )
    ) {
      return;
    }
    if (pendingAttempt) {
      resetPendingAttempt();
    }
    onClose();
  }, [creating, onClose, pendingAttempt, resetPendingAttempt, t]);

  const header = (
    <Text size="h6" weight="semibold" className="text-gray-900">
      {title}
    </Text>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      header={header}
      body={
        <div className="flex flex-col w-full max-h-[85vh]">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-4 p-1 md:p-0 flex-1 overflow-hidden">
            <div className="md:col-span-4 space-y-3 border rounded-lg p-3 bg-white flex flex-col h-full overflow-hidden">
              <div className="flex items-center justify-between">
                <Text as="label" size="tiny" className="mb-1 block">
                  Plantillas disponibles{' '}
                  {/* TODO: check when implementing i18n */}
                </Text>
                {templatesLoading && (
                  <Loading size="2xs" appearance="outline" inline />
                )}
              </div>
              {templatesError && (
                <ErrorState
                  appearance="soft"
                  size="sm"
                  className="p-2"
                  title="Error cargando plantillas" // TODO: check when implementing i18n
                  description=""
                />
              )}
              <div className="flex-1 overflow-y-auto pr-1 max-h-[60vh]">
                <TemplateCards
                  templates={templates}
                  selectedTemplateId={selectedTemplateId}
                  locked={configurationLocked}
                  loading={templatesLoading}
                  hasNextPage={Boolean(hasNextPage)}
                  fetchingNextPage={isFetchingNextPage}
                  triggerDisplay={triggerDisplay}
                  onSelect={setSelectedTemplateId}
                  onLoadMore={() => {
                    void fetchNextPage();
                  }}
                />
              </div>
              <div className="sticky bottom-0 border rounded p-2 bg-gray-50">
                <TemplateDetail
                  template={selectedTemplate}
                  loading={templateLoading}
                  triggerName={selectedTriggerName}
                />
              </div>
            </div>

            <div className="md:col-span-8 space-y-3 border rounded-lg p-3 bg-white flex flex-col max-h-[85vh]">
              {pendingAttempt && (
                <div className="rounded-lg border border-warning-300 bg-warning-50 p-3">
                  <Text
                    size="small"
                    weight="semibold"
                    className="text-warning-900"
                  >
                    {t('automations.quickHandoff.partial.title')}
                  </Text>
                  <Text size="tiny" className="text-warning-800">
                    {t('automations.quickHandoff.partial.description', {
                      workflowId: pendingAttempt.workflowId,
                    })}
                  </Text>
                </div>
              )}

              <div className="space-y-1">
                <Text as="label" size="tiny" className="mb-1 block">
                  Nombre del workflow {/* TODO: check when implementing i18n */}
                </Text>
                <TextInput
                  value={workflowName}
                  onChange={(e) => setWorkflowName(e.target.value)}
                  placeholder="Nombre para el workflow a crear" // TODO: check when implementing i18n
                  disabled={configurationLocked}
                />
              </div>

              <div className="space-y-2 mt-2 flex-1 overflow-auto">
                <div className="max-h-full overflow-auto pr-1">
                  <ConfigurationEditor
                    guidedKind={guidedKind}
                    guidedTemplateValid={guidedValidation.valid}
                    semanticKind={semanticKind}
                    semanticTemplateValid={semanticValidation.valid}
                    context={quickAutomationContext}
                    guidedConfiguration={guidedConfiguration}
                    semanticSetup={semanticSetup}
                    mapping={mapping}
                    onMappingChange={setMapping}
                    loadingEmployeePermission={loadingEmployeePermission}
                    canReadEmployees={canReadEmployees}
                    locked={configurationLocked}
                    genericEditor={
                      <>
                        <div className="flex items-center justify-between">
                          <Text as="label" size="tiny" className="mb-1 block">
                            Mapeo de entradas{' '}
                            {/* TODO: check when implementing i18n */}
                          </Text>
                          {triggersLoading && (
                            <Loading size="2xs" appearance="outline" inline />
                          )}
                        </div>
                        <fieldset disabled={configurationLocked}>
                          <InputMappingSection
                            fieldKeys={fieldKeys}
                            requiredFields={requiredFields}
                            mapping={mapping}
                            eventPathOptions={eventPathOptions}
                            fieldExpectedLiteralTypes={
                              fieldExpectedLiteralTypes
                            }
                            onChange={(key, v) =>
                              setMapping((prev) => ({ ...prev, [key]: v }))
                            }
                            emptyMessage="Este flujo de trabajo no define entradas." // TODO: check when implementing i18n
                          />
                        </fieldset>
                      </>
                    }
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      }
      footer={
        <ModalFooter
          onCancel={handleClose}
          onConfirm={onConfirm}
          cancelText="Cancelar"
          confirmText={
            pendingAttempt
              ? t('automations.quickHandoff.actions.retryActivation')
              : confirmLabel
          }
          confirmDisabled={
            creating ||
            (!pendingAttempt &&
              (!selectedTemplate ||
                !workflowName.trim() ||
                !effectiveMappingValid))
          }
          loading={creating}
          variant="primary"
        />
      }
      dialogClassName="sm:max-w-7xl lg:max-w-[1280px]"
    />
  );
};

export default CreateAutomationFromTemplateModal;
